import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "./reduxHooks";
import {
  authDetailsInitializeAction,
  getLoginUserList,
} from "../redux/actions/authAction";
import {
  getAlterationPrices,
  getAlterationSubCategories,
  getCategoryAction,
  getCategoryMaterialRelationship,
  getCategoryRelationship,
  getPricing,
  getSecondSubCategoryAction,
  getSubCategoryAction,
  getSubCategoryMaterialRelationship,
} from "../redux/actions/pricingAction";
import { CartSliceAction } from "../redux/slice/cartSlice";
import { BaseResponse, CategoryResponse } from "../typings";
import getTenantId from "./useTenantId";
import { resolveBaseTenant } from "../util/common-functions";
import { loadStoreSetup } from "../util/store-setup-loader";
import { getDiscountsByStoreId } from "../redux/actions/discountAction";
import { getAllMerchants } from "../redux/actions/merchantAction";
import { MasterSliceActions } from "../redux/slice/masterSlice";
import { getStoreConfig } from "../redux/actions/storeActions";
import { getStoreClosureDates } from "../redux/actions/storeClosureDateActions";
import { getActivePriceOverrideConfigsAction } from "../redux/actions/priceOverrideConfigAction";
import { getSpecialChargesAction } from "../redux/actions/specialChargeAction";
import {
  getCashierDetailsAction,
  getStationByIdAction,
  getStationSummaryAction,
} from "../redux/actions/cashierStationActions";
import {
  setActiveStation,
  setActiveStationCashier,
  setStationCashierHydrated,
} from "../redux/slice/cashierStationSlice";
import { IcashierOnboardingResponse } from "../typings";
import { ILocalStationCashier } from "../typings/interfaces/responses/cashierStationResponses";

const TRAY_DEVICE_URL = "http://localhost:8535/device-id";

const useAppInitializer = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { adminDashboardSelectedStore, adminDashboardSelectedMerchant } = useAppSelector((state) => state.master);

  // The admin panel takes the look (colours, logo, formats) of the store
  // picked in its header; the POS loads its own store's with the store config
  useEffect(() => {
    const tenant = getTenantId();
    const storeId = adminDashboardSelectedStore?.storeId;
    const merchantId = adminDashboardSelectedMerchant?.merchantId;
    if (!tenant || resolveBaseTenant(tenant) !== "admin" || !storeId || !merchantId) return;
    loadStoreSetup(storeId, merchantId);
  }, [adminDashboardSelectedStore?.storeId, adminDashboardSelectedMerchant?.merchantId]);
  const { getCashierStationByIdResponse } = useAppSelector(
    (state) => state.cashierStation
  );
  const tenantId = getTenantId();

  // The station grant is device-level (stored locally, not tied to the
  // logged-in user), so fetch it as soon as the app boots — no need to
  // wait until after login/redirect to the lobby. It's also re-keyed on
  // `user`: a boot-time fetch before login has no auth token yet and
  // fails, but pending/rejected both reset getCashierStationByIdResponse
  // to null (same as its initial value), so without `user` in the deps
  // the effect would never retry once the token is attached post-login.
  useEffect(() => {
    const cashier = localStorage.getItem("cashier");
    const parsedCashier: IcashierOnboardingResponse | null = cashier
      ? JSON.parse(cashier)
      : null;

    if (!getCashierStationByIdResponse && parsedCashier?.cashierPoint?.id) {
      dispatch(getStationByIdAction(parsedCashier.cashierPoint.id));
    }
  }, [getCashierStationByIdResponse, dispatch, user]);

  useEffect(() => {
    if (getCashierStationByIdResponse) {
      dispatch(setActiveStation(getCashierStationByIdResponse));
    }
  }, [getCashierStationByIdResponse, dispatch]);

  // Whether the register is actually open is device/station truth, not
  // something the logged-in user's browser can be trusted to remember: the
  // localStorage "activeCashierSession" cache goes stale the moment it's
  // cleared, or another browser/device opens or closes the same station's
  // register. Trusting it blindly (as this used to) meant Void/Refund could
  // fail with "Station session not found", and Lobby's action buttons could
  // render disabled, even though a session was genuinely open — most
  // visibly right after a fresh login with an empty/cleared localStorage.
  //
  // So re-verify against the backend every time `user` changes (covers both
  // a fresh login and a page refresh that restores a persisted token),
  // using the same device-fingerprint → getCashierDetailsAction flow the
  // onboarding page uses to learn the device's cashier point in the first
  // place. Flip stationCashierHydrated back to false for the duration so
  // PrivateRoute holds Lobby/Void/Refund on a loading screen until the
  // verified result (or a safe fallback) is in.
  useEffect(() => {
    let cancelled = false;

    const verifyStationCashierSession = async () => {
      if (!user) {
        // Not logged in yet — nothing to verify.
        dispatch(setStationCashierHydrated(true));
        return;
      }

      dispatch(setStationCashierHydrated(false));

      try {
        const deviceResponse = await fetch(TRAY_DEVICE_URL);
        if (!deviceResponse.ok) throw new Error("Tray app unreachable");
        const { deviceId } = await deviceResponse.json();
        if (!deviceId || deviceId === "UNKNOWN_DEVICE") {
          throw new Error("No device id");
        }

        // Deliberately not gated on the "cashier" localStorage cache: on a
        // freshly cleared/new browser that cache is empty even though this
        // device is already granted a station, and getCashierDetailsAction
        // is exactly what (re)populates it from the device fingerprint —
        // the same call the onboarding page makes the first time. Gating
        // on the cache here just meant a cleared browser could never
        // recover without a page refresh.
        const details = await dispatch(
          getCashierDetailsAction(deviceId)
        ).unwrap();
        const cashierPointId = details?.cashierPoint?.id;
        if (!cashierPointId) throw new Error("No cashier point for device");

        // getStationByIdAction/setActiveStation (the other effects above)
        // only fire off of the "cashier" localStorage cache and never retry
        // once it's populated later — so on a cleared browser, activeStation
        // would otherwise stay null (bouncing to /pos/onboarding) until a
        // hard refresh re-ran those effects against the now-repopulated
        // cache. Set it directly and synchronously from what we already
        // have (mirroring OnboardingPage's own onboarding flow), then also
        // kick off getStationByIdAction in the background for a fuller
        // refresh — no need to wait on that one.
        if (!cancelled && details.grantedDevice?.accessGranted === "GRANTED") {
          dispatch(setActiveStation(details));
          dispatch(getStationByIdAction(cashierPointId));
        }

        const summary = await dispatch(
          getStationSummaryAction(cashierPointId)
        ).unwrap();
        const stationAccessId = details?.activeSessionId;
        const status: "open" | "closed" =
          summary?.stationStatus === "open" ? "open" : "closed";

        if (cancelled) return;

        if (stationAccessId) {
          const verifiedSession: ILocalStationCashier = {
            cashierPointId,
            stationAccessId,
            status,
          };
          localStorage.setItem(
            "activeCashierSession",
            JSON.stringify(verifiedSession)
          );
          dispatch(setActiveStationCashier(verifiedSession));
        } else {
          // Backend has no active session for this station — don't let a
          // stale local cache keep claiming one is open.
          localStorage.removeItem("activeCashierSession");
          dispatch(setActiveStationCashier(null));
        }
      } catch (err) {
        // Tray app not running, device unrecognized, or the backend call
        // failed — fall back to whatever's cached locally rather than
        // blocking the user from working entirely.
        if (!cancelled) {
          const localCashierSession: ILocalStationCashier | null = JSON.parse(
            localStorage.getItem("activeCashierSession") || "null"
          );
          if (localCashierSession) {
            dispatch(setActiveStationCashier(localCashierSession));
          }
        }
      } finally {
        if (!cancelled) dispatch(setStationCashierHydrated(true));
      }
    };

    verifyStationCashierSession();

    return () => {
      cancelled = true;
    };
  }, [user, dispatch]);

  useEffect(() => {
    if (user === null) {
      dispatch(authDetailsInitializeAction());
    }
    if (user) {
      dispatch(getCategoryAction()).then((data) => {
        const dataPayload = data.payload as {
          response: BaseResponse<CategoryResponse>;
        };
        if (dataPayload?.response?.body?.length > 0)
          dispatch(
            CartSliceAction.setSelectedServiceType(dataPayload.response.body[0])
          );
      });
      dispatch(getCategoryRelationship());
      dispatch(getCategoryMaterialRelationship());
      dispatch(getSubCategoryMaterialRelationship());
      dispatch(getSecondSubCategoryAction());
      // dispatch(getDryCleanPriceAction());
      dispatch(getAlterationPrices());
      dispatch(getAlterationSubCategories());
      dispatch(getDiscountsByStoreId(user.storeId));
      dispatch(getStoreConfig({ storeId: user.storeId, merchantId: 1 }));
      // Ready dates skip holidays, so load them with the store config
      dispatch(getStoreClosureDates({ storeId: user.storeId, merchantId: 1 }));
      dispatch(getActivePriceOverrideConfigsAction());
      // What a Special ticket costs extra, per service
      dispatch(getSpecialChargesAction());
      if (tenantId) {
        dispatch(getAllMerchants()).then((action) => {
          if (getAllMerchants.fulfilled.match(action) && !adminDashboardSelectedStore) {
            const merchants = action.payload?.body;
            const firstMerchant = merchants?.[0];
            const firstStore = firstMerchant?.stores?.[0];
            if (firstMerchant && firstStore) {
              dispatch(MasterSliceActions.setAdminDashboardSelectedMerchant(firstMerchant));
              dispatch(MasterSliceActions.setAdminDashboardSelectedStore(firstStore));
            }
          }
        });
        dispatch(getLoginUserList(tenantId));
      }
    }
  }, [user]);
};

export { useAppInitializer };
